import { HideImage } from '@mui/icons-material'
import { Button, Dialog, DialogContent, Grid } from '@mui/material'
import { ExplanationAccordion } from 'components'
import * as controllers from 'controllers'
import { VehicleCheckInRoofPhotoDto, vehicleCheckInRoofPhotoValidationSchema } from 'dtos'
import { Formik } from 'formik'
import {
  CheckInPhotoBrowseTakeRow,
  CheckInPhotoDialogNavProps,
  CheckInPhotoDialogTitleRow,
  CheckInPhotoPreview,
  CheckInPhotoStackedNavButtons,
  useCheckInPhotoSubmitIntent,
  useLifecycleDocumentPreview
} from './checkInPhotoDialogCommon'

interface RoofPhotoDialogProps {
  initialValues: VehicleCheckInRoofPhotoDto
  isLoading: boolean
  nav?: CheckInPhotoDialogNavProps
  onClose: () => void
  onDelete: () => void
  onSave: (values: VehicleCheckInRoofPhotoDto, options?: { goToNext?: boolean }) => void
  open: boolean
  vehicleGuid: string
}

export default function RoofPhotoDialog({
  initialValues,
  isLoading,
  nav,
  onClose,
  onDelete,
  onSave,
  open,
  vehicleGuid
}: RoofPhotoDialogProps) {
  const { onFormSubmit, submitClose, submitThenNext } = useCheckInPhotoSubmitIntent(onSave)
  const serverSrc = useLifecycleDocumentPreview(
    vehicleGuid,
    initialValues.documentGuid,
    initialValues.displayFileName,
    controllers.getVehicleCheckInPhotoBase64
  )

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validateOnChange={true}
        validateOnBlur={false}
        validationSchema={vehicleCheckInRoofPhotoValidationSchema}
        onSubmit={onFormSubmit}
      >
        {({ isSubmitting, setFieldValue, submitForm, values }) => {
          return (
            <form onSubmit={e => void e.preventDefault()}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <CheckInPhotoDialogTitleRow onClose={onClose} title='Roof Photo Details' />

                  <Grid item xs={12}>
                    <CheckInPhotoPreview
                      alt='Roof'
                      file={values.vehicleCheckInRoofPhotoFile}
                      placeholderSrc='/13 Roof.jpg'
                      serverSrc={serverSrc}
                    />
                  </Grid>

                  <CheckInPhotoBrowseTakeRow
                    browseInputId='roof-browse'
                    cameraInputId='roof-camera'
                    disabled={isSubmitting || isLoading}
                    onFileChosen={file => setFieldValue('vehicleCheckInRoofPhotoFile', file)}
                  />

                  <Grid item xs={12}>
                    <Button
                      color='error'
                      disabled={isSubmitting || isLoading || !Boolean(values.documentGuid)}
                      endIcon={<HideImage />}
                      fullWidth
                      onClick={() => {
                        if (window.confirm('Are you sure you would like to delete the uploaded Roof Photo?')) {
                          onDelete()
                        }
                      }}
                      variant='outlined'
                    >
                      DELETE UPLOADED PHOTO
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <ExplanationAccordion variant='outlined'>
                      || “Delete Uploaded Photo” can only be pressed if a user has uploaded an image. || Pressing “Previous” goes
                      to the previous photo without saving. ||
                    </ExplanationAccordion>
                  </Grid>

                  <CheckInPhotoStackedNavButtons
                    isLoading={isLoading}
                    isSubmitting={isSubmitting}
                    nav={nav}
                    submitClose={submitClose}
                    submitForm={submitForm}
                    submitThenNext={submitThenNext}
                  />
                </Grid>
              </DialogContent>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
